import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const IntellectualPropertyDMCA = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <BackButton
            onClick={() => window.history.back()}
            variant="link"
            size="small"
            color="#C97B53"
            ariaLabel="Back to Legal Center"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Intellectual Property & DMCA Policy
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            Vesta respects the intellectual property rights of others and expects all users—Sellers, Buyers, and Guests—to do the same. This policy explains how we address claims of infringement and protect original creative work on the Platform.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Your Content & Rights
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>When you upload content to Vesta—including videos, photos, text, product designs, service descriptions, and branding—you retain all rights to that content.</li>
              <li>By posting content, you grant Vesta a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute your content for purposes of operating, marketing, and improving the Platform.</li>
              <li>You are responsible for ensuring that:
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-6 mt-2">
                  <li>You own the rights to your content, or you have permission to use it.</li>
                  <li>Your content does not infringe the copyright, trademark, trade secret, or other intellectual property rights of others.</li>
                </ul>
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Prohibited Infringement
            </h2>
            <p className="text-gray-700 mb-4">You may not:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Use another seller's photos, videos, or written descriptions without permission.</li>
              <li>Use copyrighted music, logos, or images in your videos without a valid license.</li>
              <li>Sell products or services that infringe on third-party patents, copyrights, or trademarks.</li>
              <li>Mislead others by using branding similar to another seller or business.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. DMCA Takedown Procedure
            </h2>
            <p className="text-gray-700 mb-4">
              Vesta follows the Digital Millennium Copyright Act ("DMCA") for copyright-related claims. If you believe your copyright is infringed, submit a written notice containing:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your contact information (name, address, phone, email).</li>
              <li>A description of the copyrighted work you claim has been infringed.</li>
              <li>The URL or location of the infringing content on Vesta.</li>
              <li>A statement that you have a good faith belief the use is not authorized.</li>
              <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on their behalf.</li>
              <li>Your physical or electronic signature.</li>
            </ul>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 font-semibold mb-2">Send DMCA notices to:</p>
              <ul className="text-blue-700">
                <li>Email: ip@vesta.com</li>
                <li>Mail: Vesta IP Officer, [Address]</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Trademark Complaints
            </h2>
            <p className="text-gray-700 mb-4">
              If you believe your trademark is being used without authorization in a way that is likely to cause confusion, send us:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your contact information.</li>
              <li>The trademark you own and registration details (if applicable).</li>
              <li>The specific Vesta listing(s) or profile(s) where the mark is used.</li>
              <li>A description of how it's being misused.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Counter-Notices
            </h2>
            <p className="text-gray-700 mb-4">
              If your content is removed due to an infringement claim and you believe the removal was a mistake or misidentification, you may file a counter-notice that includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your contact information.</li>
              <li>Identification of the removed content and its prior location on Vesta.</li>
              <li>A statement, under penalty of perjury, that you have a good faith belief the content was removed by mistake.</li>
              <li>Your consent to the jurisdiction of the U.S. federal court for your address, or if outside the U.S., to the jurisdiction of a U.S. court where Vesta is located.</li>
              <li>Your physical or electronic signature.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Repeat Infringer Policy
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users who repeatedly infringe others' intellectual property rights may have their accounts suspended or permanently terminated.</li>
              <li>Vesta maintains a three-strike system for intellectual property violations.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Content Removal Process
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Upon receipt of a valid DMCA notice, Vesta will promptly remove or disable access to the allegedly infringing content.</li>
              <li>Content will be restored if a valid counter-notice is received and no legal action is taken within 10-14 business days.</li>
              <li>Vesta may remove content immediately in cases of clear infringement or repeated violations.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Fair Use & Exceptions
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Vesta recognizes that some uses of copyrighted material may constitute fair use.</li>
              <li>Educational, commentary, and parody content may be protected under fair use doctrines.</li>
              <li>Users may submit fair use arguments as part of their counter-notice.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. International Intellectual Property Rights
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Vesta respects intellectual property rights worldwide.</li>
              <li>Users must comply with intellectual property laws in their jurisdiction.</li>
              <li>International disputes will be handled according to applicable treaties and agreements.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Trade Secrets & Confidential Information
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users must not disclose trade secrets or confidential business information.</li>
              <li>Recipes, formulas, and business processes shared on Vesta should not contain proprietary secrets.</li>
              <li>Vesta is not responsible for protecting trade secrets disclosed in public listings.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Licensing & Permissions
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users must obtain proper licenses for any third-party content used in their listings.</li>
              <li>Music, images, and other media must be properly licensed for commercial use.</li>
              <li>Free-to-use content must be verified as such before posting.</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. No Legal Advice
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Vesta does not provide legal advice.</li>
              <li>If you have questions about intellectual property rights, consult a qualified attorney.</li>
              <li>This policy is for informational purposes only and does not constitute legal counsel.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Policy Updates
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>We may revise this policy to reflect legal or operational changes.</li>
              <li>Continued use of Vesta after updates constitutes acceptance.</li>
              <li>Material changes will be communicated via email or in-app notifications.</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For intellectual property questions or concerns:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: ip@vesta.com</li>
              <li>Legal Department: legal@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Governing Law
            </h2>
            <p className="text-gray-700">
              This policy is governed by U.S. federal law, particularly the DMCA, and applicable state laws where Vesta operates.
            </p>
          </section>

          {/* Final Statement */}
          <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <p className="text-orange-800 text-center font-medium">
              By using Vesta, you acknowledge that you have read, understood, and agree to be bound by this Intellectual Property & DMCA Policy.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default IntellectualPropertyDMCA; 